import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Divider, Layout} from '@phonectas/unifon-ui-kit';
import CustomTabs from '../CustomTabs';
import UpsellMessageBox from './components/UpsellMessageBox';
import SecurityProducts from './components/SecurityProducts';
import PrivateSubscriptions from './components/PrivateSubscriptions';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import {ThreeSixtyStoreState} from 'common/types/store/threeSixtyTypes';
import {UpsellOrderModel} from 'common/models/upsellModel';
import {useCalculateUpsellCommission} from 'common/hooks/useCalculateUpsellCommission';
import {UPSELL_CONTENT_MSG, UPSELL_NO_USER_MSG} from './constants';


const UppsellContent: React.FC = () => {
	const [t] = useTranslation();
	const [activeTab, setActiveTab] = useState('1');
	const {getUpsellUser} = useThreeSixtyNewStore((state) => ({getUpsellUser: state.getUpsellUser}), shallow);
	const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
	const {
		data,
		product,
		subscriptions,
		activeRow,
		bnRedirect
	} = useThreeSixtyNewStore((state: ThreeSixtyStoreState) => ({
		data: state?.upsellDetails,
		product: state?.upsellDetails?.upsell_products || [],
		subscriptions: state?.upsellDetails?.upsell_subscriptions || [],
		activeRow: state?.activeRow,
		bnRedirect: state?.bnRedirect?.account || null,
	}), shallow);

	const commission = useCalculateUpsellCommission(
		product,
		subscriptions
	);

	const tabs = useMemo(() => {
		return [
			{
				value: '1',
				label: t('Security Products'),
				disabled: !data?.upsell_products || data.upsell_products.length === 0,
			},
			{
				value: '2',
				label: t('Private Subscriptions'),
				disabled: !data?.upsell_subscriptions || data.upsell_subscriptions.length === 0,
			},
		];
	}, [data, t]);

	const upsellOrder = useMemo(() => {
		if (data) {
			return new UpsellOrderModel(data?.account_id, data?.user_id);
		}
		return new UpsellOrderModel(0, 0);
	}, [data]);

	const userPhone = useMemo(() => {
		return activeRow?.phone_number
			? String(activeRow.phone_number)
			: bnRedirect?.account_details?.user_details?.phone_number || '';
	}, [activeRow, bnRedirect]);


	const handleSafeZoneSelection = (
		cdrProductId: number,
		safeZoneCdrId: number,
		safeZonePlusCdrId: number,
		tempOrder: UpsellOrderModel
	) => {
		if (cdrProductId === safeZoneCdrId) {
			if (tempOrder.cdr_product_ids.includes(safeZoneCdrId)) {
				// If Safe Zone is already selected, remove it
				tempOrder.removeProduct(safeZoneCdrId);
			} else {
				// If Safe Zone is not selected, add it and remove Safe Zone+
				tempOrder.removeProduct(safeZonePlusCdrId);
				tempOrder.addProduct(safeZoneCdrId);
			}
		} else if (cdrProductId === safeZonePlusCdrId) {
			if (tempOrder.cdr_product_ids.includes(safeZonePlusCdrId)) {
				// If Safe Zone+ is already selected, remove it
				tempOrder.removeProduct(safeZonePlusCdrId);
			} else {
				// If Safe Zone+ is not selected, add it and remove Safe Zone
				tempOrder.removeProduct(safeZoneCdrId);
				tempOrder.addProduct(safeZonePlusCdrId);
			}
		} else {
			if (tempOrder.cdr_product_ids.includes(cdrProductId)) {
				tempOrder.removeProduct(cdrProductId);
			} else {
				tempOrder.addProduct(cdrProductId);
			}
		}
	};

	const onSelect = useCallback((cdrProductId: number) => {
		// cdr_product_id for "Safe Zone"
		const safeZoneCdrId = 123;
		// cdr_product_id for "Safe Zone+"
		const safeZonePlusCdrId = 124;

		setSelectedProducts(() => {
			// Update the existing upsellOrder instance directly
			handleSafeZoneSelection(cdrProductId, safeZoneCdrId, safeZonePlusCdrId, upsellOrder);

			// Sync the selected products with upsellOrder.cdr_product_ids
			return [...upsellOrder.cdr_product_ids];
		});
	}, [upsellOrder]);

	const handleRemoveProduct = useCallback(
		(productId: number) => {
			upsellOrder.removeProduct(productId);
			setSelectedProducts(prev => prev.filter(id => id !== productId));
		},
		[upsellOrder]
	);

	const handleTabSwitch = useCallback(
		(tab: string) => {
			setActiveTab(String(tab));
		},
		[]
	);

	const badgeText = useMemo(() => {
		if (activeTab === '1') {
			return commission.productsCommission;
		} else if (activeTab === '2') {
			return commission.subscriptionsCommission;
		}
		return '';
	}, [activeTab, commission]);

	useEffect(() => {
		if (!data) {
			if (bnRedirect?.account_details?.id || activeRow?.id) {
				const stringToInt = parseInt(bnRedirect?.account_details?.id || activeRow?.id || '0', 10);
				getUpsellUser(stringToInt);
			}
		}
	}, [data, getUpsellUser, bnRedirect, activeRow]);

	return (
		<>
			<Layout direction="column" nowrap fullWidth style={{padding: '16px'}}>
				<Layout fullWidth direction="row" nowrap>
					<UpsellMessageBox
						/* i18next-extract-disable-next-line */
						message={!data ? t(UPSELL_NO_USER_MSG) : t(UPSELL_CONTENT_MSG)}
						badgeText={badgeText}
						status="ok"
					/>
				</Layout>
				<Divider />
				<CustomTabs
					radio
					items={tabs}
					active={activeTab}
					onChange={handleTabSwitch}
                
				/>
			</Layout>
			<Layout direction="column" fullWidth gap="none">
				{activeTab === '1' && (
					<SecurityProducts
						data={data}
						products={product}
						onSelect={onSelect}
						removeProduct={handleRemoveProduct}
						temp={selectedProducts}
						upsellOrder={upsellOrder}
					/>
				)}
				{activeTab === '2' && (
					<PrivateSubscriptions
						data={subscriptions}
						removeProduct={handleRemoveProduct}
						temp={selectedProducts}
						userPhone={userPhone}
					/>
				)}
			</Layout>
		</>
	);
}

export default UppsellContent;